import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";

/**
 * ColorOverridePicker — small inline control used in game editors to let
 * admin or merchant optionally override the default theme color of a piece
 * of text (e.g. the public game title or description). When no value is set
 * (null/""), the public page falls back to the theme-aware defaults.
 *
 * Provides a native color input + hex text field + preview chip + a
 * "Reset to default" link to clear the override with one click.
 */
export default function ColorOverridePicker({
  label,
  value,
  onChange,
  previewText,
  testIdPrefix,
}) {
  const { t } = useTranslation();
  const isSet = Boolean(value);
  const [hex, setHex] = useState(value || "#0f172a");

  useEffect(() => {
    if (value) setHex(value);
  }, [value]);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={hex}
          onChange={(e) => {
            setHex(e.target.value);
            onChange(e.target.value);
          }}
          className="h-9 w-9 rounded-md border border-border bg-transparent cursor-pointer"
          data-testid={`${testIdPrefix}-picker`}
        />
        <Input
          value={hex}
          onChange={(e) => {
            const v = e.target.value.trim();
            setHex(v);
            if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v);
          }}
          placeholder="#0f172a"
          className="h-9 rounded-lg text-[13px] font-mono"
          data-testid={`${testIdPrefix}-hex`}
        />
      </div>
      <div
        className="mt-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm truncate"
        style={{ color: isSet ? hex : undefined }}
        data-testid={`${testIdPrefix}-preview`}
      >
        {previewText}
      </div>
      {isSet ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
          data-testid={`${testIdPrefix}-reset`}
        >
          {t("game_editor.color_reset")}
        </button>
      ) : (
        <div className="mt-1.5 text-[11px] text-muted-foreground">
          {t("game_editor.color_using_default")}
        </div>
      )}
    </div>
  );
}
